import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTooltipPosition } from './useTooltipPosition';
import { DEFAULTS } from './constants';
import '../../styles/SmartTooltip.scss';

/**
 * SmartTooltip Component
 * Intelligent tooltip with automatic viewport boundary detection and positioning
 *
 * @param {string|JSX} content - Tooltip content (required)
 * @param {JSX} children - Hover trigger element (required)
 * @param {string} placement - "auto", "top", "bottom", "left", "right", "bottom-left", "bottom-right", etc.
 * @param {number} maxWidth - Max width in pixels (default: 320)
 * @param {number} delay - Show delay in ms (default: 300)
 * @param {object} offset - Additional offset adjustments {x, y}
 * @param {string} className - Custom CSS classes
 * @param {boolean} allowHtml - Enable dangerouslySetInnerHTML (default: false)
 * @param {boolean} disabled - Disable tooltip (default: false)
 * @param {number} zIndex - Custom z-index (default: 1000)
 */
const SmartTooltip = ({
  content,
  children,
  placement = 'auto',
  maxWidth = DEFAULTS.MAX_WIDTH,
  delay = DEFAULTS.DELAY,
  offset = { x: 0, y: 0 },
  className = '',
  allowHtml = false,
  disabled = false,
  zIndex = DEFAULTS.Z_INDEX,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);

  const { style, placement: calculatedPlacement } = useTooltipPosition(
    shouldRender,
    triggerRef,
    tooltipRef,
    placement,
    maxWidth,
    offset
  );

  // Show tooltip with delay
  const handleMouseEnter = () => {
    if (disabled || !content) return;

    timeoutRef.current = setTimeout(() => {
      setShouldRender(true);
      // Small delay for positioning calculation
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    }, delay);
  };

  // Hide tooltip immediately
  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
    // Wait for transition before unmounting
    setTimeout(() => {
      setShouldRender(false);
    }, 200);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle ESC key to close tooltip
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isVisible) {
        handleMouseLeave();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isVisible]);

  // Don't render anything if no content or disabled
  if (!content || disabled) {
    return children;
  }

  const tooltipClasses = [
    'smart-tooltip',
    calculatedPlacement,
    isVisible ? 'visible' : '',
    className
  ].filter(Boolean).join(' ');

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className={tooltipClasses}
      style={{ ...style, zIndex }}
      role="tooltip"
      aria-hidden={!isVisible}
    >
      {allowHtml ? (
        <div dangerouslySetInnerHTML={{ __html: content }} />
      ) : (
        content
      )}
    </div>
  );

  // Clone the child element and add event handlers and ref to it
  const enhancedChild = React.cloneElement(React.Children.only(children), {
    ref: triggerRef,
    onMouseEnter: (e) => {
      handleMouseEnter();
      // Call original onMouseEnter if it exists
      if (children.props.onMouseEnter) {
        children.props.onMouseEnter(e);
      }
    },
    onMouseLeave: (e) => {
      handleMouseLeave();
      // Call original onMouseLeave if it exists
      if (children.props.onMouseLeave) {
        children.props.onMouseLeave(e);
      }
    }
  });

  return (
    <>
      {enhancedChild}
      {shouldRender && createPortal(tooltipContent, document.body)}
    </>
  );
};

export default SmartTooltip;
